import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus, Plus, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const PRICE_PER_PACK = 3;
const TICKERS_PER_PACK = 5;

const features = [
  "Real-time volume spike alerts",
  "Email + SMS notifications",
  "Custom alert thresholds",
  "Pre-market & after-hours coverage",
  "Historical data access",
  "Priority support",
];

const Pricing = () => {
  const [packs, setPacks] = useState(1);

  const totalTickers = packs * TICKERS_PER_PACK;
  const totalPrice = packs * PRICE_PER_PACK;

  const increment = () => setPacks((p) => Math.min(p + 1, 20));
  const decrement = () => setPacks((p) => Math.max(p - 1, 1));

  return (
    <section id="pricing" className="py-20 md:py-28 px-6">
      <div className="container mx-auto">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <p className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
            Pricing
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Pay only for what you need.
          </h2>
          <p className="text-lg text-muted-foreground">
            $3 per 5 tickers. Scale up or down anytime — no contracts, no tiers, no BS.
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          {/* Main pricing card */}
          <div className="relative bg-card rounded-2xl border border-border/60 shadow-lg overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-accent via-accent/70 to-cta" />

            <div className="p-8 md:p-10">
              {/* Price display */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 text-accent px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                  <Zap className="h-3 w-3 fill-current" />
                  Simple pricing
                </div>

                <div className="flex items-baseline justify-center gap-1 mb-1">
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={totalPrice}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      className="text-6xl md:text-7xl font-bold tracking-tight"
                    >
                      ${totalPrice}
                    </motion.span>
                  </AnimatePresence>
                  <span className="text-muted-foreground text-lg">/mo</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  for{" "}
                  <span className="font-semibold text-foreground">
                    {totalTickers} tickers
                  </span>
                </p>
              </div>

              {/* Calculator */}
              <div className="bg-muted/60 rounded-xl p-5 mb-8">
                <label className="block text-sm font-medium text-center mb-4">
                  How many tickers do you need?
                </label>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={decrement}
                    disabled={packs <= 1}
                    className="w-11 h-11 rounded-xl bg-background border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Minus className="h-4 w-4" />
                  </button>

                  <div className="min-w-[120px] text-center">
                    <AnimatePresence mode="popLayout">
                      <motion.div
                        key={totalTickers}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="text-4xl font-bold text-accent"
                      >
                        {totalTickers}
                      </motion.div>
                    </AnimatePresence>
                    <p className="text-xs text-muted-foreground mt-0.5">tickers</p>
                  </div>

                  <button
                    onClick={increment}
                    disabled={packs >= 20}
                    className="w-11 h-11 rounded-xl bg-background border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                {/* Visual breakdown */}
                <div className="mt-4 pt-4 border-t border-border/60 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {packs} pack{packs > 1 ? "s" : ""} × ${PRICE_PER_PACK}
                  </span>
                  <span className="font-semibold">${totalPrice}/mo</span>
                </div>
              </div>

              {/* CTA */}
              <Button
                variant="cta"
                size="lg"
                className="w-full text-base font-semibold group"
                asChild
              >
                <Link to="/signup">
                  Get started with {totalTickers} tickers
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </Button>

              {/* Features */}
              <ul className="mt-8 space-y-3">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom note */}
          <div className="text-center mt-8 space-y-2">
            <p className="text-sm text-muted-foreground">
              30-day money-back guarantee · Cancel anytime · No credit card to start
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
